import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaUsers, FaChartLine, FaCalendarAlt, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import { fetchUserGrowthAnalytics } from '../../../../utils/api/adminApi.js';
import '../css/UserGrowthAnalytics.css';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import '../../AdminDash/css/AdminDash.css';
import '../../../../components/css/AdminAnalytics.css';

function UserGrowthAnalytics() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0,
    newUsersThisMonth: 0,
    newUsersToday: 0,
    growthRate: 0,
    monthlyGrowth: [],
    userTypes: [],
    registrationTrends: []
  });

  useEffect(() => {
    fetchUserGrowthData();
  }, []);

  const fetchUserGrowthData = async () => {
    try {
      setLoading(true);
      const data = await fetchUserGrowthAnalytics();

      setAnalyticsData({
        totalUsers: data.totalUsers ?? 0,
        newUsersThisMonth: data.newUsersThisMonth ?? 0,
        newUsersToday: data.newUsersToday ?? 0,
        growthRate: data.growthRate ?? 0,
        monthlyGrowth: Array.isArray(data.monthlyGrowth) ? data.monthlyGrowth : [],
        userTypes: Array.isArray(data.userTypes) ? data.userTypes : [],
        registrationTrends: Array.isArray(data.registrationTrends) ? data.registrationTrends : []
      });
    } catch (error) {
      console.error('Error fetching user growth analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  // Prepare data for OverviewCards component
  const overviewCards = [
    {
      icon: <FaUsers />,
      title: 'Total Users',
      value: analyticsData.totalUsers.toLocaleString(),
      subtitle: 'All registered users',
      color: '#EFB034FF'
    },
    {
      icon: <FaUserPlus />,
      title: 'New This Month',
      value: analyticsData.newUsersThisMonth.toLocaleString(),
      subtitle: `${analyticsData.growthRate > 0 ? '+' : ''}${analyticsData.growthRate}% growth`,
      color: '#EFB034FF'
    },
    {
      icon: <FaCalendarAlt />,
      title: 'New Today',
      value: analyticsData.newUsersToday,
      subtitle: 'Daily registrations',
      color: '#EFB034FF'
    },
    {
      icon: <FaChartLine />,
      title: 'Growth Rate',
      value: `${analyticsData.growthRate}%`,
      subtitle: 'Month over month',
      color: '#EFB034FF'
    }
  ];

  // Prepare data for charts
  const userTypesColors = ['#c3e1f8', '#b8f5cd', '#f8cedb', '#bcbff5'];
  const filteredUserTypes = analyticsData.userTypes.filter((userType) =>
    typeof userType?.type === 'string' && userType.type.toLowerCase() !== 'user'
  );

  const pieChartData = filteredUserTypes.map(userType => ({
    name: userType.type,
    value: userType.count,
    percentage: userType.percentage
  }));

  const monthlyGrowthInsight = (() => {
    const data = analyticsData.monthlyGrowth;
    if (!data || data.length < 2) return null;

    const latest = data[data.length - 1];
    const previous = data[data.length - 2];
    if (!previous.users || previous.users === 0) return null;

    const deltaPercent = ((latest.users - previous.users) / previous.users) * 100;
    return {
      type: deltaPercent >= 0 ? 'positive' : 'negative',
      icon: deltaPercent >= 0 ? <FaArrowUp /> : <FaArrowDown />,
      title: 'Month-over-month growth',
      description: `Registrations ${deltaPercent >= 0 ? 'increased' : 'decreased'} by ${Math.abs(deltaPercent).toFixed(1)}% compared to ${previous.month}.`
    };
  })();

  const weekendVsWeekdayInsight = (() => {
    const data = analyticsData.registrationTrends;
    if (!data || data.length === 0) return null;

    const weekendDays = ['sat', 'sun'];
    const totals = data.reduce(
      (acc, entry) => {
        const dayLabel = (entry.date || '').toLowerCase();
        if (weekendDays.includes(dayLabel.slice(0, 3))) {
          acc.weekend += entry.registrations || 0;
        } else {
          acc.weekday += entry.registrations || 0;
        }
        return acc;
      },
      { weekend: 0, weekday: 0 }
    );

    const total = totals.weekend + totals.weekday;
    if (total === 0) return null;

    const weekendShare = (totals.weekend / total) * 100;
    const weekdayShare = 100 - weekendShare;

    return {
      type: weekendShare >= weekdayShare ? 'positive' : 'neutral',
      icon: <FaCalendarAlt />,
      title: 'Weekend vs weekday activity',
      description: `Weekend registrations represent ${weekendShare.toFixed(1)}% of total signups.`
    };
  })();

  const userTypeInsight = (() => {
    const data = filteredUserTypes;
    if (!data || data.length === 0) return null;

    const total = data.reduce((sum, type) => sum + (type.count || 0), 0);
    if (total === 0) return null;

    const topType = data.reduce((prev, current) => (current.count > prev.count ? current : prev));
    const share = (topType.count / total) * 100;

    return {
      type: 'neutral',
      icon: <FaUsers />,
      title: 'Dominant user type',
      description: `${topType.type} accounts for ${share.toFixed(1)}% of your user base.`
    };
  })();

  const insights = [monthlyGrowthInsight, weekendVsWeekdayInsight, userTypeInsight].filter(Boolean);

  if (loading) {
    return (
      <div className="user-growth-analytics">
        <GlobalHeader
          title="User Growth Analytics"
          subtitle="Loading user data..."
          onLogout={handleLogout}
          backTo="/admin"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-growth-analytics">
      <GlobalHeader
        title="User Growth Analytics"
        subtitle="Track user registration trends and growth patterns"
        onLogout={handleLogout}
        backTo="/admin"
      />

      <main className="user-growth-analytics__main">
        {/* Overview Cards */}
        <OverviewCards cards={overviewCards} />

        {/* Charts Section */}
        <section className="analytics-charts">
          <div className="chart-container">
            <h3>User Growth Trend (6 Months)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.monthlyGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#c3e1f8" strokeWidth={2} name="Total Users" />
                <Line type="monotone" dataKey="newUsers" stroke="#b8f5cd" strokeWidth={2} name="New Users" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3>Weekly Registration Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.registrationTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="registrations" fill="#f8cedb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* User Types Breakdown */}
        <section className="user-types-section">
          <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 700, color: '#333' }}>
            User Distribution by Type
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '20px',
              maxWidth: '1250px',
            }}
          >
            {pieChartData.map((entry, index) => (
              <div
                key={entry.name}
                style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  padding: '28px',
                  textAlign: 'center',
                  borderLeft: `6px solid ${userTypesColors[index % userTypesColors.length]}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {entry.name}
                </span>
                <span style={{ fontSize: '36px', fontWeight: 700, color: '#1f2933' }}>
                  {entry.value.toLocaleString()}
                </span>
                <span style={{ fontSize: '16px', fontWeight: 500, color: userTypesColors[index % userTypesColors.length] }}>
                  {entry.percentage?.toFixed(1) ?? '0'}%
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Growth Insights */}
        <section className="growth-insights">
          <h3>Growth Insights</h3>
          {insights.length ? (
            <div className="insights-grid">
              {insights.map((insight, index) => (
                <div key={index} className={`insight-card ${insight.type}`}>
                  <div className="insight-icon">{insight.icon}</div>
                  <div className="insight-content">
                    <h4>{insight.title}</h4>
                    <p>{insight.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="insight-card neutral">
              <div className="insight-content">
                <h4>No insights yet</h4>
                <p>Connect the analytics endpoint to start surfacing growth insights.</p>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default UserGrowthAnalytics;
